import {ApiRoute, ConstantApiParamKey} from '$constants/api';
import {appendObjectToFormData} from '$modules/booking/components/LastStepForm/utils';
import store from '$redux/store';
import axiosInstance from '.';

import {
  Appointment,
  Care,
  Relationship,
  Insurance,
  BookingData,
  PaymentCard,
  City,
  Subcare,
  PaymentMode,
  Payment,
} from '../modules/booking/types';
import {bookingChoicesToAppointmentRequestBody} from '../modules/booking/utils';

interface GetBookingTypesResponseData {
  message: string;
  data: {
    id: number;
    title: string;
    description: string;
    image: string;
    is_enable: boolean;
    display_name?: string;
    isActive: boolean;
    isEnable: boolean;
  }[];
}

export async function getBookingTypes(): Promise<{
  message: string;
  data: Appointment[];
}> {
  const response = await axiosInstance.get<GetBookingTypesResponseData>(
    ApiRoute.Constant,
    {params: {key: ConstantApiParamKey.Booking}}
  );

  return {
    message: response.data.message,
    data: response.data.data.map(info => ({
      ...info,
      enable: info.is_enable,
    })),
  };
}

interface GetAppointmentTypesResponseData {
  message: string;
  data: {
    id: number;
    title: string;
    description: string;
    image: string;
    is_enable: boolean;
    isActive: boolean;
    isEnable: boolean;
  }[];
}

export async function getAppointmentTypes(
  bookingId: number
): Promise<{message: string; data: Care[]}> {
  const response = await axiosInstance.get<GetAppointmentTypesResponseData>(
    ApiRoute.Constant,
    {params: {key: ConstantApiParamKey.Appointment, appointment_id: bookingId}}
  );

  return {
    message: response.data.message,
    data: response.data.data.map(info => ({
      ...info,
      enable: info.is_enable,
    })),
  };
}

interface GetCareTypesResponseData {
  message: string;
  data: {
    id: number;
    title: string;
    description: string;
    is_enable: boolean;
    isActive: boolean;
    isEnable: boolean;
  }[];
}

export async function getCareTypes(
  appointmentId?: number
): Promise<{message: string; data: Subcare[]}> {
  const response = await axiosInstance.get<GetCareTypesResponseData>(
    ApiRoute.Constant,
    {
      params: {
        key: ConstantApiParamKey.Care,
        care_id: appointmentId,
      },
    }
  );

  return {
    message: response.data.message,
    data: response.data.data.map(info => ({
      ...info,
      enable: info.is_enable,
    })),
  };
}

export interface GetConsultantResponseData {
  message: string;
  data: {
    id: number;
    profile_url: string | null;
    full_name: string;
    doctor_details: {
      bio_link: string;
      consultation_fee: number;
      followup_fee: number;
      specializations: {
        id: number;
        title: string;
      }[];
      description: string;
    };
    price_lists: {
      consultation_fee: number;
      followup_fee: number;
    };
  }[];
}

export interface GetConsultantParams {
  subCareId?: number;
  appointmentTypeId?: number;
  withoutFee?: boolean;
}

export async function getConsultants(
  params?: GetConsultantParams
): Promise<GetConsultantResponseData> {
  const response = await axiosInstance.get<GetConsultantResponseData>(
    ApiRoute.Constant,
    {
      params: params
        ? {
            key: ConstantApiParamKey.Consultant,
            sub_care_type_id: params.subCareId,
            appointment_id: params.appointmentTypeId,
            without_fee: params.withoutFee,
          }
        : {
            key: ConstantApiParamKey.Consultant,
          },
    }
  );

  return response.data;
}

interface GetRelationshipResponseBody {
  message: string;
  data: {
    id: number;
    name: string;
  }[];
}

export async function getRelationships(): Promise<{
  message: string;
  data: Relationship[];
}> {
  const response = await axiosInstance.get<GetRelationshipResponseBody>(
    ApiRoute.Constant,
    {params: {key: ConstantApiParamKey.Relationship}}
  );

  return {
    message: response.data.message,
    data: response.data.data,
  };
}

export interface GetCitiesResponseData {
  message: string;
  data: City[];
}

export interface GetCitiesParams {
  countryId?: string;
}

export async function getCities(
  params: GetCitiesParams
): Promise<GetCitiesResponseData> {
  const {countryId} = params;

  const response = await axiosInstance.get<GetCitiesResponseData>(
    ApiRoute.Constant,
    {
      params: {key: ConstantApiParamKey.City, country: countryId},
    }
  );

  return {
    message: response.data.message,
    data: response.data.data,
  };
}

export interface PostLoginResponseData {
  data: {
    refresh_token: string;
    token: string;
    patient: {
      id: number;
      nhs_number: number;
      full_name: string;
      first_name: string;
      last_name: string;
      dob: string;
      uniq_code: string;
      prefix: string;
      country_code: string;
      mobile_number: string;
      email: string;
      gender: string;
      marital_status: string;
      status: string;
      preferred_point_of_contact: string;
      address: {
        id: number;
        line1: string;
        line2: string;
        city: string;
        state: string;
        country: string;
        postcode: string;
        addressable_type: string;
        addressable_id: number;
      };
      last_appointment_details: {
        contact_mobile_number: string;
      };
    };
  };
  message: string;
}

export interface PostLoginErrorResponseData {
  message: string;
}

export interface PostLoginVariables {
  email: string;
  password: string;
}

export async function postBookingSignIn(variables: PostLoginVariables) {
  const {email, password} = variables;

  const response = await axiosInstance.post<PostLoginResponseData>(
    ApiRoute.BookingSignIn,
    {user: {email, password}}
  );
  return response.data;
}

export interface PostPatientEmailVerificationResponseData {
  message: string;
}

export interface PostPatientEmailPayload {
  patient: {
    email: string;
  };
}

export interface ValidatePostcodeResponse {
  success: boolean;
  message: string;
}

export interface ValidatePostcodeVariables {
  postcode: string;
  country: string;
}

export async function postValidatePostcode(
  payload: ValidatePostcodeVariables
): Promise<ValidatePostcodeResponse> {
  const response = await axiosInstance.post<ValidatePostcodeResponse>(
    `${ApiRoute.ValidatePostcode}?postcode=${payload.postcode}&country=${payload.country}`,
    payload
  );
  return response.data;
}

export interface PostPatientEmailErrorResponseData {
  message: string;
}

export async function postVerifyPatientContactEmail(
  payload: PostPatientEmailPayload
): Promise<PostPatientEmailVerificationResponseData> {
  const response =
    await axiosInstance.post<PostPatientEmailVerificationResponseData>(
      ApiRoute.PatientContactEmailVerification,
      payload
    );
  return response.data;
}

export interface GetInsuranceTypesResponseData {
  message: string;
  data: {
    id: number;
    name: string;
  }[];
}

export async function getInsuranceTypes(): Promise<{
  message: string;
  data: Insurance[];
}> {
  const response = await axiosInstance.get<GetInsuranceTypesResponseData>(
    ApiRoute.Constant,
    {params: {key: ConstantApiParamKey.Insurance}}
  );

  return {
    message: response.data.message,
    data: response.data.data,
  };
}

export interface GetPaymentTypesResponseData {
  message: string;
  data: {
    id: number;
    name: string;
  }[];
}

export async function getPaymentTypes(): Promise<{
  message: string;
  data: PaymentMode[];
}> {
  const response = await axiosInstance.get<GetPaymentTypesResponseData>(
    ApiRoute.Constant,
    {params: {key: ConstantApiParamKey.Payment}}
  );

  const paymentData: PaymentMode[] = response.data.data.map(item => ({
    id: item.id,
    name: item.name,
  }));

  return {
    message: response.data.message,
    data: paymentData,
  };
}

interface DeleteAppointmentResponseData {
  message: string;
}

interface DeleteAppointmentVariables {
  id: number;
}

export async function deleteAppointment(
  variables: DeleteAppointmentVariables
): Promise<DeleteAppointmentResponseData> {
  const {id} = variables;

  const response = await axiosInstance.delete<DeleteAppointmentResponseData>(
    `${ApiRoute.Appointment}/${id}`
  );

  return response.data;
}

export interface AppointmentRequestData {
  appointment: {
    id?: number;
    sub_care_type_id?: number;
    appointment_type_id?: number;
    care_type_id?: number;
    date?: string;
    doctor_id?: number;
    preferred_time?: string;
    start_time?: string;
    end_time?: string;
    referred_by_physician?: boolean;
    referral?: File | null;
    reason?: string;
    self_booking?: boolean;
    self_paying?: boolean;
    payment_mode?: string;
    hospital_id?: number | null;
    preferred_point_of_contact?: string;
    insurance_id?: number;
    membership_no?: string;
    country_code?: string;
    contact_mobile_number?: string;
    contact_email?: string;

    patient: {
      id?: number;
      nhs_number?: number;
      prefix?: string;
      first_name?: string;
      last_name?: string;
      dob?: string;
      gender?: string;
      country_code?: string;
      mobile_number?: string | null;
      email?: string;
      password?: string;
      preferred_point_of_contact?: string | null;

      address: {
        id?: number;
        line1?: string;
        line2?: string | null;
        city?: string;
        country?: string;
        postcode?: string;
      };
    };
    insurance_claim: {
      insurance_id: number | null;
      membership_no: string;
      pre_authorisation_code: string;
    };

    care_taker?: {
      first_name?: string;
      last_name?: string;
      country_code?: string;
      mobile_number?: number;
      email?: string;
      password?: string;
      relationship?: string;
    } | null;
  };
}

export interface PostCreateAppointmentErrorResponse {
  success: string;
  errors: string[];
  message: string;
}
export interface PostCreateAppointmentResponseData {
  message: string;
  data: {
    appointment: {
      id: number;
      patient: {
        id: number;
        address: {
          id: number;
        };
      };
      doctor_name: string;
    };
  };
}

export interface PostCreateAppointmentVariables {
  choices: BookingData;
}

export async function postCreateAppointment(
  variables: PostCreateAppointmentVariables
) {
  const {choices} = variables;

  const body = bookingChoicesToAppointmentRequestBody(choices);

  const formData = new FormData();
  appendObjectToFormData(formData, body);

  const response = await axiosInstance.post<PostCreateAppointmentResponseData>(
    ApiRoute.Appointment,
    formData
  );
  return response.data;
}

// TODO: Not used following post appointment function anywhere, can remove it
export interface PostAppointmentResponseData {
  message: string;
}

export interface PostAppointmentVariables {
  id: number;
  choices: BookingData;
}

export async function postAppointment(variables: PostAppointmentVariables) {
  const {choices} = variables;

  const body = bookingChoicesToAppointmentRequestBody(choices);

  const response = await axiosInstance.post<PostAppointmentResponseData>(
    ApiRoute.Appointment,
    body
  );

  return response.data;
}

interface SendOtpRequestData {
  email: string;
}

interface SendOtpResponseData {
  success: boolean;
  data: [];
  message: string;
}

export async function sendOtp(email: string): Promise<SendOtpResponseData> {
  const requestData: SendOtpRequestData = {email};

  const response = await axiosInstance.post<SendOtpResponseData>(
    ApiRoute.BookingSendOtp,
    requestData
  );

  return response.data;
}
export interface PutForgotPasswordResponseData {
  message: string;
}

export interface PutForgotPasswordVariables {
  email: string;
  platformName: string;
}

export async function putForgotPassword(variables: PutForgotPasswordVariables) {
  const {email, platformName} = variables;

  const response = await axiosInstance.put<PutForgotPasswordResponseData>(
    ApiRoute.PatientForgotPassword,
    {patient: {email, platform_name: platformName}}
  );

  return response.data;
}

interface ReSendOtpRequestData {
  email: string;
}

interface ReSendOtpResponseData {
  success: boolean;
  data: [];
  message: string;
}

export async function ResendOtp(email: string): Promise<ReSendOtpResponseData> {
  const requestData: ReSendOtpRequestData = {email};

  const response = await axiosInstance.post<ReSendOtpResponseData>(
    ApiRoute.BookingReSendOtp,
    requestData
  );

  return response.data;
}

interface ValidateOtpRequestData {
  email: string;
  otp: number;
}

export interface PostValidateOtpResponseData {
  success: boolean;
  data: {
    refresh_token: string;
    token: string;
    patient: {
      id: number;
      nhs_number: number;
      full_name: string;
      first_name: string;
      last_name: string;
      dob: string;
      uniq_code: string;
      prefix: string;
      country_code: string;
      mobile_number: string;
      email: string;
      gender: string;
      marital_status: string;
      status: string;
      preferred_point_of_contact: string;
      address: {
        id: number;
        line1: string;
        line2: string;
        city: string;
        state: string;
        country: string;
        postcode: string;
        addressable_type: string;
        addressable_id: number;
      };
    };
    is_patient_with_full_details: boolean;
  };
  message: string;
}

export async function validateOtp(
  email: string,
  otp: number
): Promise<PostValidateOtpResponseData> {
  const requestData: ValidateOtpRequestData = {email, otp};

  const response = await axiosInstance.post<PostValidateOtpResponseData>(
    ApiRoute.BookingValidateOtp,
    requestData
  );

  return response.data;
}

export interface PutResetPasswordResponseData {
  message: string;
}

export interface PutResetPasswordVariables {
  token: string;
  password: string;
  confirmPassword: string;
}

export async function putResetPassword(variables: PutResetPasswordVariables) {
  const {password, confirmPassword, token} = variables;

  const response = await axiosInstance.put<PutResetPasswordResponseData>(
    ApiRoute.PatientResetPassword,
    {patient: {password, confirm_password: confirmPassword, token}}
  );

  return response.data;
}

export interface PutSetPasswordResponseData {
  message: string;
}

export interface PutSetPasswordVariables {
  password: string;
  confirmPassword: string;
}

export async function putSetPassword(variables: PutSetPasswordVariables) {
  const {password, confirmPassword} = variables;
  const state = store.getState();
  const token = state.auth.token;

  const response = await axiosInstance.put<PutSetPasswordResponseData>(
    ApiRoute.PatientSetPassword,
    {patient: {password, confirm_password: confirmPassword}},
    {
      headers: {
        Authorization: token,
      },
    }
  );

  return response.data;
}

export async function getPaymentCard(): Promise<{
  message: string;
  data: PaymentCard[];
}> {
  const response = await axiosInstance.get<GetInsuranceTypesResponseData>(
    ApiRoute.Constant,
    {params: {key: ConstantApiParamKey.CardType}}
  );

  return {
    message: response.data.message,
    data: response.data.data,
  };
}

export interface GetPreferredPointOfContactResponseData {
  message: string;
  data: {id: number; name: string}[];
}

export async function getPreferredPointOfContact() {
  const response =
    await axiosInstance.get<GetPreferredPointOfContactResponseData>(
      ApiRoute.Constant,
      {
        params: {key: ConstantApiParamKey.PreferredPointOfContact},
      }
    );

  return response.data;
}

export interface GetConstantsRequestVariables {
  consultant_id: string;
}

export interface GetConstantsResponse {
  success: boolean;
  data: {
    amount: number;
  };
  message: string;
}

export const getAmount = async ({
  consultant_id,
}: GetConstantsRequestVariables): Promise<GetConstantsResponse> => {
  const response = await axiosInstance.get<GetConstantsResponse>(
    ApiRoute.Constant,
    {
      params: {
        key: ConstantApiParamKey.Amount,
        consultant_id,
      },
    }
  );
  return response.data;
};
export interface GetCountryListResponseData {
  success: boolean;
  data: {code: string; name: string; country_code: string}[];
  message: string;
}

export async function getCountries() {
  const response = await axiosInstance.get<GetCountryListResponseData>(
    ApiRoute.Constant,
    {
      params: {key: ConstantApiParamKey.Country},
    }
  );

  return response.data;
}

export interface GetCancellationReasonsResponseData {
  message: string;
  data: {
    id: number;
    name: string;
  }[];
}

export async function getCancellationReasons(): Promise<{
  message: string;
  data: {id: number; name: string}[];
}> {
  const response = await axiosInstance.get<GetCancellationReasonsResponseData>(
    ApiRoute.Constant,
    {
      params: {key: ConstantApiParamKey.PatientBookingCancellation},
    }
  );

  return {
    message: response.data.message,
    data: response.data.data,
  };
}

export interface PostBlockSlotsVariables {
  date: string;
  doctorId: number;
  startTime: string;
  endTime: string;
  id: number | undefined;
  appointment_type_id: number;
  sub_care_type_id: number;
}

export interface PostBlockSlotsResponseData {
  data: {
    appointment: {
      id: number;
      start_time: string;
      end_time: string;
      date: string;
      consultant: {
        id: number;
        profile_url: string | null;
        full_name: string;
        doctor_details: {
          bio_link: string;
          consultation_fee: number;
          followup_fee: number;
          specializations: {
            id: number;
            title: string;
          }[];
          description: string;
        } | null;
        price_lists: {
          consultation_fee: number;
          followup_fee: number;
        } | null;
      };
    };
  };
}

export async function postBlockSlots(variables: PostBlockSlotsVariables) {
  const {
    date,
    doctorId,
    startTime,
    endTime,
    id,
    appointment_type_id,
    sub_care_type_id,
  } = variables;
  const response = await axiosInstance.post<PostBlockSlotsResponseData>(
    ApiRoute.BlockSlots,
    {
      appointment: {
        id: id,
        date: date,
        doctor_id: doctorId,
        start_time: startTime,
        end_time: endTime,
        appointment_type_id: appointment_type_id,
        sub_care_type_id: sub_care_type_id,
      },
    }
  );

  return response.data;
}

export interface ProcessCardPaymentRequest {
  payment: Payment;
  appointment: AppointmentRequestData['appointment'];
}

export interface CreatePaymentIntentRequest {
  amount: number;
  currency: string;
}

export interface CreatePaymentIntentResponse {
  success: boolean;
  client_secret: string;
}

export interface ProcessCardPaymentResponse {
  success: boolean;
  message: string;
  data?: {
    appointment: {
      id: number;
      start_time: string | null;
      end_time: string | null;
      date: string;
      status: string;
      appointment_type_name: string;
      created_at: string;
      preferred_time: string;
      patient_name: string;
      doctor_name: string;
      confirm_at: string | null;
      sub_care_type_name: string;
      amount: number | null;
      reason: string;
      payment_mode: string | null;
      self_booking: boolean;
      self_paying: boolean;
      membership_no: string;
      referred_by_physician: boolean;
      check_in_time: string | null;
      check_out_time: string | null;
      cancellation_reason: string | null;
      other_cancellation_reason: string | null;
      country_code: string;
      contact_email: string;
      contact_mobile_number: string;
      preferred_point_of_contact: string;
      referral_url: string;
      patient: {
        id: number;
        full_name: string;
        uniq_code: string | null;
        mobile_number: string;
        dob: string;
        email: string;
        profile_url: string | null;
        first_name: string;
        last_name: string;
        country_code: string;
        prefix: string;
        hospital_id: number;
        status: string | null;
        registration_date: string;
        nhs_number: string;
        patient_platform_first_login: boolean;
        is_deleted: boolean;
        address: {
          id: number;
          line1: string;
          line2: string;
          city: string;
          state: string | null;
          country: string;
          postcode: string;
          country_name: string;
          city_name: string;
        };
        marital_status: string | null;
        gender: string;
        last_appointment_details: {
          date: string;
          country_code: string;
          contact_mobile_number: string;
        } | null;
        special_cares: null;
        invitation_details: null;
        registration_status: boolean;
      };
      care_taker: {
        id: number;
        full_name: string;
        first_name: string;
        last_name: string;
        mobile_number: string;
        email: string;
        country_code: string;
        prefix: string | null;
        hospital_id: number;
        status: string | null;
        uniq_code: string | null;
        address: null;
        dob: string | null;
        marital_status: string | null;
        gender: string | null;
        last_appointment_details: {
          date: string;
          country_code: string;
          contact_mobile_number: string;
        } | null;
      } | null;
      doctor: {
        id: number;
        profile_url: string | null;
        full_name: string;
      };
      insurance: null;
      appointment_type: {
        id: number;
        title: string;
        display_name: string;
      };
      care_type: {
        id: number;
        title: string;
      };
      sub_care_type: {
        id: number;
        title: string;
      };
      patient_profile_exist: boolean;
      payment: {
        id: number;
        amount: number;
        patient_id: number;
        description: string;
        patient_full_name: string;
        invoice_number: string;
        billing_date: string;
        payment_method: string;
        status: string;
      };
      insurance_claim: {
        insurance_id: number;
        approved_amount: number | null;
        claimed_amount: number;
        membership_no: string;
        pre_authorisation_code: number;
        status: string;
      } | null;
    };
    approve_url: string | null;
  };
}
export const processCardPayment = async (
  params: ProcessCardPaymentRequest
): Promise<ProcessCardPaymentResponse> => {
  const response = await axiosInstance.post<ProcessCardPaymentResponse>(
    ApiRoute.Payments,
    params
  );
  return response.data;
};

export const processPaypalPayment = async (
  params: ProcessCardPaymentRequest
): Promise<ProcessCardPaymentResponse> => {
  const response = await axiosInstance.post<ProcessCardPaymentResponse>(
    ApiRoute.Payments,
    params
  );
  return response.data;
};

export const capturePaypalPayment = async (params: {
  order_id: string;
  is_link_view: boolean;
}): Promise<ProcessCardPaymentResponse> => {
  const token = store.getState().auth.token;
  const response = await axiosInstance.post<ProcessCardPaymentResponse>(
    ApiRoute.paypalCapture,
    params,
    {
      headers: {
        Authorization: token,
      },
    }
  );

  return response.data;
};

export const createPaymentIntent = async (
  params: CreatePaymentIntentRequest
): Promise<CreatePaymentIntentResponse> => {
  const response = await axiosInstance.post<CreatePaymentIntentResponse>(
    ApiRoute.CreatePaymentIntent,
    params
  );
  return response.data;
};
export interface StripePaymentMethod {
  id: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
}
export interface StripePaymentIntent {
  id: string;
  amount: number;
  status: string;
  client_secret: string;
}

export interface CancelAppointmentVariables {
  id: number;
  cancellationReason: number;
  otherCancellationReason?: string;
  payment_id?: number;
}

export interface CancelAppointmentResponseData {
  success: boolean;
  message: string;
}
export async function cancelAppointment(variables: CancelAppointmentVariables) {
  const {
    id,
    cancellationReason,
    otherCancellationReason = '',
    payment_id,
  } = variables;

  const token = store.getState().auth.token;

  const response = await axiosInstance.patch<CancelAppointmentResponseData>(
    `${ApiRoute.Appointment}/${id}/cancel`,
    {
      appointment: {
        cancellation_reason: cancellationReason,
        other_cancellation_reason: otherCancellationReason,
        payment_id: payment_id,
        status: 'cancel',
      },
    },
    token ? {headers: {Authorization: token}} : undefined
  );

  return response.data;
}
